import { BehaviorSubject, Observable, of, distinctUntilChanged, first, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ConverterService, OccEndpointsService, Product, PRODUCT_NORMALIZER, ProductSearchPage, RoutingService } from '@spartacus/core';

import { CssHierarchy, CustomCategory } from '../models';

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  searchPage: BehaviorSubject<ProductSearchPage>;
  private categories: { [code: string]: BehaviorSubject<CustomCategory> } = {};

  private products: Product[];

  constructor(
    private routingService: RoutingService,
    private occEndpointsService: OccEndpointsService,
    private http: HttpClient,
    private converter: ConverterService
  ) {}

  getCurrentCategory(): Observable<CustomCategory | null> {
    return this.routingService.getRouterState().pipe(
      switchMap((routerState) => {
        const state = routerState.nextState ?? routerState.state;
        const categoryCode = state.params['categoryCode'];
        const isSis = state.url.includes('/csc/');
        return categoryCode ? this.getCategory(categoryCode, isSis) : of(null);
      }),
      distinctUntilChanged()
    );
  }

  getProductsCategorySis(page: number, pageSize: number): Observable<ProductSearchPage> {
    return this.getCurrentCategory().pipe(
      first((category) => !!category),
      tap((category) => {
        this.products = this.converter.convertMany(category.products, PRODUCT_NORMALIZER);
        const searchPage: ProductSearchPage = {
          products: this.products?.slice(0, pageSize) || [],
          pagination: {
            currentPage: page,
            pageSize,
            totalPages: Math.ceil(this.products?.length / pageSize),
            totalResults: this.products?.length
          }
        };
        if (page !== 0) {
          this.searchPage = new BehaviorSubject<CustomCategory>(searchPage);
          this.goToPage(page, pageSize);
        } else {
          if (this.searchPage) {
            this.searchPage.next(searchPage);
          } else {
            this.searchPage = new BehaviorSubject<CustomCategory>(searchPage);
          }
        }
      }),
      switchMap(() => this.searchPage.asObservable())
    );
  }

  nextPage(nextPage: number, pageSize: number): void {
    const start = nextPage * pageSize;
    const end = start + pageSize;
    const products = this.products?.slice(start, end);
    const searchPage: ProductSearchPage = {
      products,
      pagination: {
        currentPage: nextPage,
        pageSize,
        totalPages: Math.ceil(this.products?.length / pageSize),
        totalResults: this.products?.length
      }
    };
    this.searchPage.next(searchPage);
  }

  goToPage(page: number, pageSize: number): void {
    const start = page * pageSize;
    const end = start + pageSize;
    const products = this.products?.slice(start, end);
    const searchPage: ProductSearchPage = {
      products,
      pagination: {
        currentPage: page,
        pageSize,
        totalPages: Math.ceil(this.products?.length / pageSize),
        totalResults: this.products?.length
      }
    };
    this.searchPage.next(searchPage);
  }

  getCategory(categoryCode: string, isSis: boolean): Observable<CustomCategory> {
    if (!this.categories[categoryCode]) {
      this.categories[categoryCode] = new BehaviorSubject<CustomCategory>(undefined);
      this.loadCategory(categoryCode, isSis);
    }
    return this.categories[categoryCode].asObservable();
  }

  private loadCategory(categoryCode: string, isSis: boolean): void {
    if (isSis) {
      const url = this.occEndpointsService.buildUrl('categoryCssHierarchy', {
        urlParams: { categoryCode },
        queryParams: { type: 'ACCESSORY', selectedCategory: categoryCode }
      });
      this.http.get<CssHierarchy>(url).subscribe((category) => {
        this.categories[categoryCode].next(category.accessoryCategory);
      });
    } else {
      const url = this.occEndpointsService.buildUrl('categories', { urlParams: { categoryCode } });
      this.http.get<CustomCategory>(url).subscribe((category) => {
        this.categories[categoryCode].next(category);
      });
    }
  }
}
